/*
 * Copyright (c) 2019. Sonrai Analytics Ltd.
 * Developed by Deva Sen., Ryan Duffy & Warren Dowey
 */

import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { Router } from '@angular/router';
import { interval, Observable, of, Subscription } from 'rxjs';
import { delay, filter, switchMap, tap } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MyNotificationModel } from '@app/models/my-notification.model';
import { MyDetailedNotificationsService } from '@app/services/shared-services/my-detailed-notifications.service';
import { SubSink } from '@base/node_modules//subsink/dist/subsink';
import { OrganisationCognitoUser } from '@app/models/cognito-organisation-user.model';
import { OrganisationService } from '@app/services/api-services/organisations/organisation.service';
import { environment } from '@env/environment';
import { OrganisationModel } from '@app/models/organisation.model';
import { MyNotificationListService } from '@app/services/api-services/my-notifications/my-notifications-list.service';
import { ImageParserService } from '@app/services/shared-services/image-parser.service';
import { AuthenticationService } from '@app/services/authentication/authentication.service';
import { ConnectionService } from 'app/services/shared-services/connection.service';
import { NotificationService } from '@app/services/api-services/notifications/notification.service';

@Component({
  selector: 'app-user-box',
  templateUrl: '../../../../../../../../../src/app/components/layouts/header/elements/user-box/user-box.component.html',
  styleUrls: ['../../../../../../../../../src/app/components/layouts/header/elements/user-box/user-box.component.scss'],
})
export class UserBoxComponent implements OnInit, OnDestroy {
  @Input() userBoxClass: string = 'bottomFloat';
  @Input() sidebarUserBox: boolean = true;
  @Input() avatarSize: number = 50;
  @Input() showBadge: boolean = true;
  @Output() onNotificationAlert = new EventEmitter();
  public user: OrganisationCognitoUser;
  public userFullName: string;
  protected heading: string;
  protected email: string;
  public newNotificationsCount: number;
  public myNotificationsList: MyNotificationModel[];
  isShow = false;
  userImage: Observable<string>;

  @ViewChild('newNotificationsModal', {}) notificationsModel;

  @ViewChild('checkbox', {}) checkBox: ElementRef;

  platformLogo: Observable<string>;
  private organisationSubscription: Subscription;
  private timeoutSubscription: Subscription;
  // Subsink to be used in the OnDestroy Lifecycle hook to destroy all active subscriptions
  private subscriptionContainer = new SubSink();

  /**
   * Creates an instance of UserBoxComponent.
   *
   * @constructor
   * @param {AuthenticationService} authService
   * @param {Router} router
   * @param {NgbModal} modalService
   * @param {MyDetailedNotificationsService} myDetailedNotificationsService
   * @param {OrganisationService} organisationService
   * @param {NotificationService} notificationService
   * @param {ImageParserService} imageParser
   * @param {ConnectionService} connectionService
   */
  constructor(
    public authService: AuthenticationService,
    public router: Router,
    private modalService: NgbModal,
    private myDetailedNotificationsService: MyDetailedNotificationsService,
    private organisationService: OrganisationService,
    private notificationService: NotificationService,
    private imageParser: ImageParserService,
    private connectionService: ConnectionService
  ) {}

  /**
   * TODO Description placeholder
   */
  ngOnInit() {
    this.user = this.authService.user;
    this.userImage = this.imageParser.parse(this.user.displayPicture);
    this.getPlatformLogo();
    if (this.user) {
      this.userFullName = `${this.user.firstName} ${this.user.familyName}`;
    }
    // Get the count of new notifications to display to the user
    this.subscriptionContainer.sink = this.myDetailedNotificationsService
      .getNewCount()
      .pipe(delay(0))
      .subscribe((count) => {
        this.newNotificationsCount = count;
        this.onNotificationAlert.emit(this.newNotificationsCount);
      });
    this.checkForNewNotifications();
  }
  /**
   *  get the logo for the current organsiation
   *
   * @memberof UserBoxComponent
   */
  getPlatformLogo(): void {
    let organisationContainer: OrganisationModel;
    this.organisationService.getOrganisationById(environment.product_owner_uuid)
      .subscribe((organisation) => {
        organisationContainer = organisation;
        this.platformLogo = this.imageParser.parse(organisationContainer.platformLogo);
        if (this.organisationSubscription) {
          this.organisationSubscription.unsubscribe();
        }
      });
  }
  // Open the create task model using Ngb
  /**
   * TODO Description placeholder
   *
   * @returns {*}
   */
  openNotificationsModal(): any {
    this.modalService.open(this.notificationsModel, {
      size: 'xl',
      scrollable: true,
      windowClass: 'z-index-modal',
      backdrop: true,
      backdropClass: 'z-index-backdrop',
    });
  }
  /**
   *
   *
   * @memberof UserBoxComponent
   */
  logout() {
    this.subscriptionContainer.sink = this.authService.logout().subscribe(() => {
      this.router.navigate(['/login']);
    });
  }
  /**
   *  on a 10 second interval check for new notifications
   * and update the count and list in the sidebar
   *
   * @memberof UserBoxComponent
   */
  checkForNewNotifications() {
    this.subscriptionContainer.sink = this.timeoutSubscription = interval(60000)
      .pipe(
        switchMap(() => {
          return this.connectionService.monitor();
        }),
        filter((value) => value),
        switchMap(() => {
          if (this.authService.isAuthenticated()) {
            return this.notificationService.getMyNotificationsByUserId(this.user.userId);
          } else {
            return of([]);
          }
        })
      )
      .subscribe(() => {});
  }
  /**
   *  open the modal display - set timeout so the directive comes first in the event loop
   *
   * @memberof UserBoxComponent
   */
  openDisplay() {
    // Check if isShow is true or false in order to toggle display on click
    if (this.isShow === false) {
      setTimeout(() => {
        this.isShow = true;
      }, 0);
    } else {
      this.isShow = false;
    }
  }
  /**
   *  close the window using the assocated directive - will reduce event listener leak
   *
   * @param {*} event
   * @memberof UserBoxComponent
   */
  closeWindow(event) {
    if (this.isShow) {
      event.stopPropagation();
      this.isShow = false;
    }
  }

  /**
   * TODO Description placeholder
   */
  ngOnDestroy() {
    this.subscriptionContainer.unsubscribe();
  }
}
